@if (visible()) {
  <button type="button" class="dsb-fab" (click)="toggle()"
          [attr.aria-expanded]="open()" aria-label="Scanner une pièce comptable">
    <span class="material-icons-outlined">{{ open() ? 'close' : 'document_scanner' }}</span>
  </button>
}

@if (open()) {
  <div class="dsb-backdrop" (click)="close()"></div>
  <div class="dsb-panel" role="dialog" aria-modal="true" aria-label="Scanner reçu ou facture">
    <header class="dsb-head">
      <div>
        <strong>Scanner une pièce</strong>
        <small>Marges live · multi-prises · sélection auto · Cursor ACP</small>
      </div>
      <button type="button" class="dsb-x" (click)="close()" aria-label="Fermer">
        <span class="material-icons-outlined">close</span>
      </button>
    </header>

    <div class="dsb-steps" aria-hidden="true">
      <span [class.on]="step() === 'capture' || step() === 'analyzing'">1 · Photos</span>
      <span [class.on]="step() === 'remediate'">2 · Contrôle</span>
      <span [class.on]="step() === 'result'">3 · Écriture</span>
    </div>

    @if (step() === 'capture' || step() === 'analyzing') {
      <div class="dsb-types">
        <button type="button" [class.active]="docType() === 'receipt'" (click)="setType('receipt')">
          <span class="material-icons-outlined">receipt_long</span> Reçu
        </button>
        <button type="button" [class.active]="docType() === 'purchase_invoice'" (click)="setType('purchase_invoice')">
          <span class="material-icons-outlined">shopping_bag</span> Fournisseur
        </button>
        <button type="button" [class.active]="docType() === 'sales_invoice'" (click)="setType('sales_invoice')">
          <span class="material-icons-outlined">point_of_sale</span> Client
        </button>
      </div>

      <div class="dsb-toggles">
        <button type="button" class="dsb-chip" [class.on]="autoMargins()" (click)="toggleAutoMargins()">
          <span class="material-icons-outlined">crop_free</span>
          Marges auto
        </button>
        <button type="button" class="dsb-chip" [class.on]="autoSelect()" (click)="toggleAuto()">
          <span class="material-icons-outlined">auto_awesome</span>
          Sélection auto
        </button>
        <button type="button" class="dsb-chip" [class.on]="multiDocs()" (click)="toggleMulti()">
          <span class="material-icons-outlined">filter_none</span>
          Plusieurs docs
        </button>
        @if (cameraOn() && step() === 'capture') {
          <button type="button" class="dsb-chip ghost" (click)="resetMargins()" title="Réinitialiser les marges">
            <span class="material-icons-outlined">restart_alt</span>
            Reset cadre
          </button>
        }
      </div>

      <div class="dsb-cam" #camBox [class.off]="!cameraOn()">
        <video #videoEl class="dsb-video" playsinline muted autoplay
               [class.hide]="!cameraOn() || step() === 'analyzing'"></video>

        @if (cameraOn() && step() === 'capture') {
          <!-- videoReady() force le recalcul contain après metadata -->
          <div class="dsb-mask" [attr.data-vr]="videoReady()" [ngStyle]="videoLayerStyle()" aria-hidden="true">
            <div class="dsb-crop" [ngStyle]="cropStyle()"
                 (pointerdown)="onMarginPointerDown('move', $event)">
              <span class="dsb-crop-label">
                {{ autoMargins() ? 'Auto' : 'Manuel' }}
                @if (marginConf() > 0) {
                  · {{ (marginConf() * 100) | number:'1.0-0' }}%
                }
              </span>
              <i class="dsb-h n" (pointerdown)="onMarginPointerDown('n', $event)"></i>
              <i class="dsb-h s" (pointerdown)="onMarginPointerDown('s', $event)"></i>
              <i class="dsb-h e" (pointerdown)="onMarginPointerDown('e', $event)"></i>
              <i class="dsb-h w" (pointerdown)="onMarginPointerDown('w', $event)"></i>
              <i class="dsb-c nw" (pointerdown)="onMarginPointerDown('nw', $event)"></i>
              <i class="dsb-c ne" (pointerdown)="onMarginPointerDown('ne', $event)"></i>
              <i class="dsb-c sw" (pointerdown)="onMarginPointerDown('sw', $event)"></i>
              <i class="dsb-c se" (pointerdown)="onMarginPointerDown('se', $event)"></i>
            </div>
          </div>
        }

        @if (!cameraOn() && step() === 'capture') {
          <div class="dsb-cam-fallback">
            <span class="material-icons-outlined">videocam_off</span>
            <p>{{ cameraError() || 'Caméra arrêtée' }}</p>
            <button type="button" class="dsb-mini" (click)="startCamera()">Activer la caméra</button>
          </div>
        }
        @if (cameraOn() && step() === 'capture') {
          <div class="dsb-cam-tools" role="toolbar" aria-label="Contrôles caméra">
            @if (camCaps().torch) {
              <button type="button" class="dsb-cam-btn"
                      [class.on]="torchOn()"
                      (click)="toggleTorch()"
                      [disabled]="camBusy()"
                      [attr.aria-pressed]="torchOn()"
                      [attr.aria-label]="torchOn() ? 'Éteindre la torche' : 'Allumer la torche'">
                <span class="material-icons-outlined">{{ torchOn() ? 'flashlight_on' : 'flashlight_off' }}</span>
              </button>
            }
            @if (camCaps().canFlip) {
              <button type="button" class="dsb-cam-btn"
                      (click)="flipCamera()"
                      [disabled]="camBusy()"
                      aria-label="Basculer caméra avant / arrière">
                <span class="material-icons-outlined">cameraswitch</span>
              </button>
            }
            @if (camCaps().zoom) {
              <button type="button" class="dsb-cam-btn"
                      (click)="nudgeZoom(-1)"
                      [disabled]="camBusy() || zoomLevel() <= camCaps().zoomMin"
                      aria-label="Zoom arrière">
                <span class="material-icons-outlined">remove</span>
              </button>
              <label class="dsb-zoom">
                <span class="sr-only">Zoom</span>
                <input type="range"
                       [min]="camCaps().zoomMin"
                       [max]="camCaps().zoomMax"
                       [step]="camCaps().zoomStep || 0.1"
                       [value]="zoomLevel()"
                       (input)="onZoomInput($event)" />
                <em>×{{ zoomLevel() | number:'1.0-1' }}</em>
              </label>
              <button type="button" class="dsb-cam-btn"
                      (click)="nudgeZoom(1)"
                      [disabled]="camBusy() || zoomLevel() >= camCaps().zoomMax"
                      aria-label="Zoom avant">
                <span class="material-icons-outlined">add</span>
              </button>
            }
          </div>
        }

        @if (cameraOn() && liveScore() != null) {
          <div class="dsb-live" [class]="scoreClass(liveScore()!)">
            <strong>{{ liveScore() }}</strong>
            <span>{{ liveHint() }}</span>
          </div>
        }
        @if (step() === 'analyzing') {
          <div class="dsb-analyzing">
            <span class="material-icons-outlined spin">progress_activity</span>
            Analyse Cursor ACP…
          </div>
        }
      </div>

      @if (step() === 'capture') {
        <div class="dsb-shutter-row">
          <button type="button" class="dsb-shutter" (click)="shutter()"
                  [disabled]="!cameraOn() || busy()" aria-label="Prendre une photo">
            <span class="dsb-shutter-ring"></span>
          </button>
          <div class="dsb-shutter-meta">
            <strong>{{ shots().length }} prise{{ shots().length > 1 ? 's' : '' }}</strong>
            <small>{{ selectedShots().length }} sélectionnée{{ selectedShots().length > 1 ? 's' : '' }}
              · auto {{ autoSelect() ? 'ON' : 'OFF' }}</small>
          </div>
        </div>

        @if (shots().length) {
          <div class="dsb-shots" role="list">
            @for (s of shots(); track s.id) {
              <button type="button" class="dsb-shot" role="listitem"
                      [class.sel]="s.selected" [class.bad]="!s.ok"
                      (click)="toggleShot(s.id)" [attr.aria-pressed]="s.selected">
                <img [src]="s.url" alt="Prise {{ s.score }}" />
                <em [class]="scoreClass(s.score)">{{ s.score }}</em>
                <span class="dsb-shot-x" (click)="removeShot(s.id); $event.stopPropagation()"
                      aria-label="Retirer">×</span>
              </button>
            }
          </div>
        }

        <div class="dsb-actions capture">
          <button type="button" class="primary" (click)="analyzeSelected()"
                  [disabled]="busy() || !selectedShots().length">
            {{ multiDocs() && selectedShots().length > 1
               ? 'Importer ' + selectedShots().length + ' factures'
               : 'Analyser la meilleure' }}
          </button>
          <label class="dsb-file-link">
            <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.pdf"
                   multiple (change)="onFile($event)" [disabled]="busy()" hidden />
            Ou importer fichier(s) / PDF
          </label>
        </div>
      }
    }

    @if (step() === 'remediate') {
      <div class="dsb-remediate">
        <div class="dsb-alert">
          <span class="material-icons-outlined">warning_amber</span>
          <div>
            <strong>Remédiation requise</strong>
            <p>{{ reasonHint() || 'Complétez les champs avant de proposer une écriture.' }}</p>
            @if (importSummary()) {
              <p class="dsb-import-note">{{ importSummary() }}</p>
            }
          </div>
        </div>

        @if (previewUrl()) {
          <img class="dsb-preview" [src]="previewUrl()!" alt="Aperçu pièce" />
        }

        <div class="dsb-form">
          <label>
            <span>Montant TTC *</span>
            <input type="number" step="0.001" min="0" [(ngModel)]="formAmount" />
          </label>
          <label>
            <span>TVA</span>
            <input type="number" step="0.001" min="0" [(ngModel)]="formTax" />
          </label>
          <label>
            <span>Date</span>
            <input type="date" [(ngModel)]="formDate" />
          </label>
          <label>
            <span>Devise</span>
            <input type="text" maxlength="8" [(ngModel)]="formCurrency" />
          </label>
          <label class="full">
            <span>Fournisseur</span>
            <input type="text" [(ngModel)]="formSupplier" />
          </label>
          <label class="full">
            <span>Client</span>
            <input type="text" [(ngModel)]="formCustomer" />
          </label>
          <label class="full">
            <span>Référence</span>
            <input type="text" [(ngModel)]="formRef" />
          </label>
        </div>

        <div class="dsb-actions">
          <button type="button" class="primary" (click)="saveRemediation()" [disabled]="busy()">
            Valider les données
          </button>
          <button type="button" (click)="retake()" [disabled]="busy()">Reprendre les photos</button>
        </div>
      </div>
    }

    @if (error()) {
      <p class="dsb-err">{{ error() }}</p>
    }

    @if (step() === 'result' && result(); as doc) {
      <div class="dsb-result">
        @if (importSummary()) {
          <p class="dsb-import-note">{{ importSummary() }}</p>
        }
        <div class="dsb-meta">
          <span class="dsb-badge">{{ typeLabel(doc.doc_type) }}</span>
          <span class="dsb-badge soft">{{ doc.status }}</span>
          @if (doc.ai) {
            <span class="dsb-badge">OCR ACP</span>
          } @else {
            <span class="dsb-badge warn">Saisie / OCR limité</span>
          }
          @if (doc.ai_confidence != null) {
            <span class="dsb-badge soft">{{ (doc.ai_confidence * 100) | number:'1.0-0' }}%</span>
          }
        </div>
        <dl>
          <div><dt>Montant TTC</dt><dd>{{ doc.amount_ttc ?? '—' }} {{ doc.currency }}</dd></div>
          <div><dt>TVA</dt><dd>{{ doc.tax_amount ?? '—' }}</dd></div>
          <div><dt>Date</dt><dd>{{ doc.doc_date || '—' }}</dd></div>
          <div><dt>Réf.</dt><dd>{{ doc.reference || '—' }}</dd></div>
          <div><dt>Fournisseur</dt><dd>{{ doc.supplier_name || '—' }}</dd></div>
          <div><dt>Client</dt><dd>{{ doc.customer_name || '—' }}</dd></div>
        </dl>

        @if (doc.proposed_lines?.length) {
          <table class="dsb-lines">
            <thead><tr><th>Compte</th><th>Libellé</th><th>Débit</th><th>Crédit</th></tr></thead>
            <tbody>
              @for (ln of doc.proposed_lines; track $index) {
                <tr>
                  <td>{{ ln.account_code }}</td>
                  <td>{{ ln.label || '' }}</td>
                  <td>{{ ln.debit || '' }}</td>
                  <td>{{ ln.credit || '' }}</td>
                </tr>
              }
            </tbody>
          </table>
        }

        @if (doc.matches?.length) {
          <div class="dsb-matches">
            <small>Correspondances possibles</small>
            @for (m of doc.matches; track m.kind + m.id) {
              <button type="button" (click)="reconcile(m)" [disabled]="busy()">
                {{ m.label }} <em>{{ (m.score * 100) | number:'1.0-0' }}%</em>
              </button>
            }
          </div>
        }

        <div class="dsb-actions">
          @if (doc.status !== 'reconciled') {
            <button type="button" class="primary" (click)="propose()" [disabled]="busy()">
              Proposer écriture SCE
            </button>
            <button type="button" class="primary soft" (click)="reconcile()" [disabled]="busy()">
              Rapprocher &amp; poster
            </button>
            <button type="button" (click)="retake()">Nouvelles photos</button>
            <button type="button" (click)="dismiss()">Ignorer</button>
          } @else {
            <p class="dsb-ok">Pièce rapprochée et écriture postée.</p>
          }
          <button type="button" class="link" (click)="goCompta()">Ouvrir Comptabilité · Pièces</button>
        </div>
      </div>
    }
  </div>
}
